import { ButtonHTMLAttributes, PropsWithChildren } from 'react';
import { cx, useSafeHandler } from './helpers';
import { Icon, IconName, IconSize } from './icons';

type Flavor = 'primary' | 'secondary' | 'tertiary';

const flavorToClassName: Record<Flavor, string> = {
  primary: 'aura--button-primary',
  secondary: 'aura--button-secondary',
  tertiary: 'aura--button-tertiary',
};

export type ButtonProps = PropsWithChildren & {
  flavor: Flavor;
  type?: ButtonHTMLAttributes<Element>['type'];
  size: 'medium' | 'small';
  block?: boolean;
  onClick?: (event?: React.MouseEvent<HTMLElement>) => Promise<void> | void;
  disabled?: boolean;
  disruptive?: boolean;
  className?: string;
};

export function buttonClassNames({
  flavor,
  block = false,
  className,
  disruptive = false,
  size,
}: Pick<
  ButtonProps,
  'flavor' | 'size' | 'block' | 'disruptive' | 'className'
>): string {
  return cx(
    'aura--button',
    size === 'medium' ? 'aura--button-md' : 'aura--button-sm',
    flavorToClassName[flavor],
    { 'aura--button-block': block },
    { 'aura--button-disruptive': disruptive },
    className,
  );
}

export function Button({
  children,
  flavor,
  size,
  block = false,
  onClick,
  type = 'button',
  disabled,
  disruptive = false,
  className,
}: ButtonProps): JSX.Element {
  const onClickHandler = useSafeHandler(onClick);
  return (
    <button
      onClick={onClickHandler}
      disabled={disabled}
      className={buttonClassNames({
        block,
        className,
        disruptive,
        flavor,
        size,
      })}
      type={type}
    >
      {children}
    </button>
  );
}

type ButtonAsLinkProps = PropsWithChildren & {
  leftIconName?: IconName;
  rightIconName?: IconName;
  onClick?: (event?: React.MouseEvent<HTMLElement>) => Promise<void> | void;
  className?: string;
  size?: 'normal' | 'small';
  disruptive?: boolean;
  disabled?: boolean;
};

export function ButtonAsLink({
  children,
  leftIconName,
  rightIconName,
  onClick,
  className,
  size = 'normal',
  disruptive,
  disabled,
}: ButtonAsLinkProps): JSX.Element {
  const onClickHandler = useSafeHandler(onClick);
  return (
    <button
      type="button"
      className={cx(
        'aura--link-button',
        'aura--link',
        size === 'small' && 'aura--link-sm',
        disruptive && 'aura--link-disruptive',
        disabled && 'aura--link-disabled',
        className,
      )}
      onClick={onClickHandler}
      disabled={disabled}
    >
      {leftIconName && (
        <Icon name={leftIconName} size={IconSize.SMALL} color="#000000" />
      )}
      {children}
      {rightIconName && (
        <Icon name={rightIconName} size={IconSize.SMALL} color="#000000" />
      )}
    </button>
  );
}

type IconButtonProps = Pick<
  ButtonProps,
  'flavor' | 'size' | 'type' | 'onClick' | 'disruptive'
> & {
  name: IconName;
  disabled?: boolean;
  color?: string;
  backgroundColor?: string;
  ariaLabel?: string;
  className?: string;
};

export function IconButton({
  backgroundColor,
  ariaLabel,
  name,
  flavor,
  size,
  disabled,
  onClick,
  color,
  type,
  disruptive = false,
  className,
}: IconButtonProps): JSX.Element {
  const onClickHandler = useSafeHandler(onClick);
  return (
    <button
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={onClickHandler}
      className={cx(
        size === 'medium' ? 'aura--icon-button-md' : 'aura--icon-button-sm',
        flavorToClassName[flavor],
        { 'aura--button-disruptive': disruptive },
        className,
      )}
      style={{ backgroundColor }}
      type={type}
    >
      <Icon
        name={name}
        size={size === 'medium' ? IconSize.MEDIUM : IconSize.SMALL}
        color={color}
      />
    </button>
  );
}
